import React, { useState } from 'react';

interface SleepSession {
  id: string;
  patient_id: string;
  device_id: string;
  device_type?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  sleep_score: number;
  deep_sleep_minutes: number;
  rem_sleep_minutes: number;
  light_sleep_minutes: number;
  awake_minutes: number;
  interruptions: number;
  heart_rate_avg: number;
  respiratory_rate_avg: number;
  source: string;
  created_at: string;
}

interface SleepSessionsProps {
  sessions: SleepSession[];
}

const SleepSessions: React.FC<SleepSessionsProps> = ({ sessions }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getDeviceIcon = (source: string) => {
    const sourceMap: { [key: string]: string } = {
      'emfit': '🛏️',
      'apple_watch': '⌚',
      'oura': '💍',
      'biostrap': '📱'
    };
    return sourceMap[source.toLowerCase()] || '🌙';
  };

  if (sessions.length === 0) {
    return null; // Don't show anything if no sessions
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left hover:bg-gray-50 -m-2 p-2 rounded-lg transition-colors"
      >
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <span className="mr-2">🌙</span>
          Sleep Sessions
          <span className="ml-2 text-sm font-normal text-gray-500">({sessions.length} sessions)</span>
        </h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {isExpanded ? 'Click to collapse' : 'Click to expand'}
          </span>
          <span className={`text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-4 mt-4 pt-4 border-t border-gray-100">
          {sessions.map((session) => (
            <div key={session.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="text-3xl">{getDeviceIcon(session.source)}</div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {formatDate(session.start_time)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatTime(session.start_time)} - {formatTime(session.end_time)}
                    </div>
                    <div className="text-xs text-gray-500 capitalize mt-1">
                      {session.source.replace('_', ' ')}
                    </div>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full font-semibold ${getScoreColor(session.sleep_score)}`}>
                  {session.sleep_score}/100
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded p-3">
                  <div className="text-xs text-gray-600 mb-1">Total Sleep</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatDuration(session.duration_minutes)}
                  </div>
                </div>

                <div className="bg-blue-50 rounded p-3">
                  <div className="text-xs text-gray-600 mb-1">Deep Sleep</div>
                  <div className="text-lg font-semibold text-blue-900">
                    {formatDuration(session.deep_sleep_minutes)}
                  </div>
                  <div className="text-xs text-blue-600">
                    {((session.deep_sleep_minutes / session.duration_minutes) * 100).toFixed(0)}%
                  </div>
                </div>

                <div className="bg-purple-50 rounded p-3">
                  <div className="text-xs text-gray-600 mb-1">REM Sleep</div>
                  <div className="text-lg font-semibold text-purple-900">
                    {formatDuration(session.rem_sleep_minutes)}
                  </div>
                  <div className="text-xs text-purple-600">
                    {((session.rem_sleep_minutes / session.duration_minutes) * 100).toFixed(0)}%
                  </div>
                </div>

                <div className="bg-amber-50 rounded p-3">
                  <div className="text-xs text-gray-600 mb-1">Light Sleep</div>
                  <div className="text-lg font-semibold text-amber-900">
                    {formatDuration(session.light_sleep_minutes)}
                  </div>
                  <div className="text-xs text-amber-600">
                    {((session.light_sleep_minutes / session.duration_minutes) * 100).toFixed(0)}%
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-gray-500">Awake:</span>
                  <span className="font-medium text-gray-900">{formatDuration(session.awake_minutes)}</span>
                </div>

                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-gray-500">Interruptions:</span>
                  <span className="font-medium text-gray-900">{session.interruptions || 0}</span>
                </div>

                {session.heart_rate_avg != null && (
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="text-gray-500">Avg HR:</span>
                    <span className="font-medium text-gray-900">{Number(session.heart_rate_avg).toFixed(0)} bpm</span>
                  </div>
                )}

                {session.respiratory_rate_avg != null && (
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="text-gray-500">Avg RR:</span>
                    <span className="font-medium text-gray-900">{Number(session.respiratory_rate_avg).toFixed(1)} br/min</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SleepSessions;
